import { SearchResponse } from '@/src/types/flight'
import { getSplitId } from '../searchUtils/getSplitId'

/**
 * Which per-card price the filter labels reflect:
 * - 'multiAirlinePrice' (default): combined/incremental RT price — solution view.
 * - 'splitLegFare': the leg's INDIVIDUAL fare — RT split view (so filter prices match the cards).
 */
export type LegPriceField = 'multiAirlinePrice' | 'splitLegFare'

const cardPrice = (card: any, priceField: LegPriceField) =>
    (card?.priceBreakup?.[priceField] ?? card?.priceBreakup?.pr) || 0

/**
 * Cheapest-first card-id order FOR THE PRICE FIELD being labelled. `price_up` is sorted by
 * `multiAirlinePrice` (the combined RT delta), so a split-view (`splitLegFare`) read must use
 * `oneWayPrice_up` — special-RT cards sit at a very different rank in the two orders.
 */
const priceSortedIds = (data: SearchResponse, leg: 0 | 1, priceField: LegPriceField) =>
    (priceField === 'splitLegFare' ? data?.sortData?.oneWayPrice_up?.[leg] : undefined) ??
    data?.sortData?.price_up?.[leg]

export const stopsFilterPriceModifier = (
    flightResultResponseData: SearchResponse,
    leg: 0 | 1,
    priceField: LegPriceField = 'multiAirlinePrice'
) => {
    const targetCards = flightResultResponseData?.cards?.[leg]
    const targetStopsFilter = flightResultResponseData?.filterData?.NewStops?.[leg]
    const targetPriceUpSort = priceSortedIds(flightResultResponseData, leg, priceField)

    targetStopsFilter?.options?.forEach((stopType, stopTypeIndex) => {
        const targetCardId = targetPriceUpSort?.find((cardId, cardIdIndex) => {
            const cardIndex = getSplitId(cardId)?.[1]
            const card = targetCards?.[Number(cardIndex)]
            const maxStopsInSectors = card?.maxStopsInSectors
            return maxStopsInSectors === stopType
        })
        if (targetCardId) {
            const targetCard = targetCards?.[Number(getSplitId(targetCardId)?.[1])]
            targetStopsFilter.prices[stopTypeIndex] = cardPrice(targetCard, priceField)
        }
    })
}

export const airlineFilterPriceModifier = (
    flightResultResponseData: SearchResponse,
    leg: 0 | 1,
    priceField: LegPriceField = 'multiAirlinePrice'
) => {
    const targetCards = flightResultResponseData?.cards?.[leg]
    const targetAirlineFilter = flightResultResponseData?.filterData?.airlineFilter?.[leg]
    const targetPriceUpSort = priceSortedIds(flightResultResponseData, leg, priceField)

    targetAirlineFilter?.options?.forEach((airline, airlineIndex) => {
        const targetCardId = targetPriceUpSort?.find((cardId, cardIdIndex) => {
            const cardIndex = getSplitId(cardId)?.[1]
            const card = targetCards?.[Number(cardIndex)]
            const airlineCodes = card.airlineCodes
            return airlineCodes.includes(airline.value)
        })
        if (targetCardId) {
            const targetCard = targetCards[Number(getSplitId(targetCardId)?.[1])]
            targetAirlineFilter.options[airlineIndex].priceLabel = cardPrice(targetCard, priceField)
        }
    })
}

export const priceRangeFilterPriceModifier = (
    flightResultResponseData: SearchResponse,
    leg: 0 | 1,
    priceField: LegPriceField = 'multiAirlinePrice'
) => {
    const targetCards = flightResultResponseData?.cards?.[leg]
    const targetPriceRangeFilter = flightResultResponseData?.filterData?.journeyPrice?.[leg]
    // Scan the cards for the bounds instead of reading the ends of a sort array: the slider must span
    // the exact field filterCards compares against, and no sort order is guaranteed to rank by it.
    let minPrice = Infinity
    let maxPrice = 0
    targetCards?.forEach(card => {
        const price = cardPrice(card, priceField)
        if (price < minPrice) minPrice = price
        if (price > maxPrice) maxPrice = price
    })
    if (minPrice === Infinity) minPrice = 0

    targetPriceRangeFilter.min = minPrice || 0
    targetPriceRangeFilter.max = maxPrice || 0
    targetPriceRangeFilter.current.max = maxPrice || 0
    targetPriceRangeFilter.current.min = minPrice || 0
}
